import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Heading, SvgIcon } from '@gik/ui';
import { Calendar } from './Calendar';
import { CalendarUtil, useCollectRecipientInfo } from '@gik/calendar';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useUserStore } from '@gik/core/store/UserStore';
import { printElement } from '@gik/core/utils/browser';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/CustomHooks';
import { inkindPageTourSelectors } from '@gik/inkind-page';

export interface ICalendarSectionProps extends React.HTMLAttributes<HTMLDivElement> {}

export function CalendarSection({ className, ...otherProps }: ICalendarSectionProps): React.ReactElement {
  const bem = bemBlock('inkind-calendar-section');
  const { t } = useTranslation();
  const collectRecipientInfo = useCollectRecipientInfo();

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const userId = useUserStore(state => state.id);
  const canEdit = canUserEditPage(inkind, userId);

  const mainRef = React.useRef<HTMLDivElement>();

  async function handleAddRequest() {
    await collectRecipientInfo();
    CalendarUtil.add();
  }

  function handlePrint() {
    printElement(mainRef.current);
  }

  const isXsDown = useBreakpoint(Breakpoint.XS_DOWN);

  return (
    <div className={bem(null, null, className)} {...otherProps}>
      <header className={bem('header', null, 'no-print')}>
        <Heading level={2} className={bem('title')}>
          Care Calendar
        </Heading>
        <div className={bem('buttons-wrapper')}>
          {canEdit && (
            <Button
              id={
                isXsDown
                  ? inkindPageTourSelectors.addCalendarRequestMobile
                  : inkindPageTourSelectors.addCalendarRequestDesktop
              }
              variant="secondary"
              circle={isXsDown}
              onClick={handleAddRequest}
              hideLabelOnMobile
              prepend={<SvgIcon name="Plus" />}
            >
              {t(translationKeys.addRequest)}
            </Button>
          )}
          <Button className="gik-hidden-md-down" onClick={handlePrint} prepend={<SvgIcon name={'Printer'} />}>
            {t(translationKeys.printMonth)}
          </Button>
        </div>
      </header>
      <div ref={mainRef}>
        <Calendar />
      </div>
    </div>
  );
}
